/**
 * KYC Verification Page - Identity verification step
 *
 * Screen for launching KYC/identity verification SDK
 * Part of the sequential flow: Calculator → Phone → Name → Address → Date of Birth → ID → KYC Verification → Digital Offramp Info → Review → Status
 */

import { t } from "@lingui/core/macro";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { RampsButton } from "@/components/ramps/Button";
import { KycTimer } from "@/components/ramps/KycTimer";
import { KycVerification } from "@/components/ramps/KycVerification";
import { RampsText } from "@/components/ramps/Text";
import { useContinue } from "@/hooks/useContinue";
import { useInitiateKycCheck } from "@/hooks/useInitiateKycCheck";
import { useKycCheck } from "@/hooks/useKycCheck";
import { useAnalytics } from "@/hooks/usePostHog";
import { navigateWithRampsState } from "@/lib/ramps-navigation";
import { toast } from "@/lib/toast.tsx";
import { trackError } from "@/utils/analytics-helpers";
import { rampsStore } from "../stores/ramps-store";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";

// Type for SDK completion data
interface KycVerificationData {
	face?: { id: string; variant: string };
	document_front?: { id: string; type: string; side: string };
	document_back?: { id: string; type: string; side: string };
	[key: string]: unknown;
}

type SdkCompletionStatus = "idle" | "completed" | "error" | "exited";
type InitiationStatus = "idle" | "pending" | "success" | "error";

type KycCheckStatusType =
	| "idle"
	| "checking"
	| "complete"
	| "declined"
	| "pending"
	| "timeout";

function getKycMessage(
	kycCheckStatus?: KycCheckStatusType,
	initiationStatus?: InitiationStatus,
) {
	// Show initiation message while API call is pending
	if (initiationStatus === "pending") {
		return t`Initiating identity verification.`;
	}
	// Show error message if initiation failed
	if (initiationStatus === "error") {
		return t`Cannot verify identity at this time. Please try again later.`;
	}
	// Messages based on kycCheckStatus
	if (kycCheckStatus === "complete") {
		return t`Identity verified successfully!`;
	}
	if (kycCheckStatus === "declined" || kycCheckStatus === "timeout") {
		return t`Verification is taking longer than expected. Please try again later.`;
	}
	return t`We're verifying your identity. This usually takes under 2 minutes.`;
}

function KycVerificationPage() {
	const search = Route.useSearch();
	const navigate = useNavigate();
	const handleContinue = useContinue();
	const analytics = useAnalytics();
	const { trackPageView } = analytics;

	// Selective store subscriptions - only subscribe to fields we need
	// This prevents rerenders when kycCheckStatus or other unrelated fields change
	const kycDataSource = useRampsStore((state) => state.kycDataSource);
	const kycToken = useRampsStore((state) => state.kycToken);
	const kycApplicantId = useRampsStore((state) => state.kycApplicantId);
	const digitalProfileId = useRampsStore((state) => state.digitalProfileId);
	const kycCheckStatus = useRampsStore((state) => state.kycCheckStatus);
	const kycPollingTime = useRampsStore((state) => state.kycPollingTime);
	const isAllowedToProceed = useRampsStore((state) => state.isAllowedToProceed);

	// Track page view
	useEffect(() => {
		trackPageView("kyc-verification", { page_name: "kyc-verification" });

		// Check if running on HTTPS (required for camera access)
		if (
			typeof window !== "undefined" &&
			window.location.protocol !== "https:" &&
			window.location.hostname !== "localhost"
		) {
			console.warn(
				"⚠️ Camera access requires HTTPS. You're currently on HTTP. Camera may not work.",
			);
		}
	}, [trackPageView]);

	// Determine if we have API-provided KYC data
	const hasApiData = kycDataSource === "api" && !!kycToken && !!kycApplicantId;

	// Use API data if available, otherwise use manual inputs
	const tokenForSdk = hasApiData ? kycToken : "";
	const applicantIdForSdk = hasApiData ? kycApplicantId : "";

	const [showSdk, setShowSdk] = useState(false);
	const [sdkCompletionStatus, setSdkCompletionStatus] =
		useState<SdkCompletionStatus>("idle");
	const [initiationStatus, setInitiationStatus] =
		useState<InitiationStatus>("idle");
	const [timerSeconds, setTimerSeconds] = useState<number | null>(null);
	const [isTimerActive, setIsTimerActive] = useState(false);
	const [shouldPoll, setShouldPoll] = useState(false);

	// Ref to track if we've already attempted to launch SDK to prevent infinite loops
	const hasLaunchedSdkRef = useRef(false);

	// Hook for initiating KYC check
	const initiateKycCheckMutation = useInitiateKycCheck();

	// TanStack Query polling hook for KYC check status
	// Only poll if SDK completed, initiation succeeded, and polling is enabled
	const {
		data: checkResult,
		isError,
		error,
	} = useKycCheck({
		enabled:
			shouldPoll &&
			sdkCompletionStatus === "completed" &&
			initiationStatus === "success",
		digitalProfileId: digitalProfileId || "",
	});

	// Handle SDK completion - initiate KYC check API call
	const handleComplete = useCallback(
		(data?: KycVerificationData) => {
			// Store the completion status and SDK response data (extracts document IDs)
			rampsActions.setKycVerificationCompleted(data);
			rampsActions.setStepUpRequired(false);

			setSdkCompletionStatus("completed");
			setShowSdk(false);

			// Get current store values after document IDs are set
			const currentDocumentIds = rampsStore.state.kycDocumentIds;
			const currentApplicantId = rampsStore.state.kycApplicantId;
			const currentDigitalProfileId = rampsStore.state.digitalProfileId;

			// Validate we have all required data before making API call
			if (
				!currentApplicantId ||
				currentDocumentIds.length === 0 ||
				!currentDigitalProfileId
			) {
				console.error("❌ Missing required data for KYC check initiation:", {
					applicantId: currentApplicantId,
					documentIds: currentDocumentIds,
					digitalProfileId: currentDigitalProfileId,
				});
				setInitiationStatus("error");
				toast.error(t`Something has gone wrong`);
				return;
			}

			// Set initiation status to pending
			setInitiationStatus("pending");

			// Call the initiate KYC check API
			initiateKycCheckMutation.mutate(
				{
					applicantId: currentApplicantId,
					documentIds: currentDocumentIds,
					digitalProfileId: currentDigitalProfileId,
				},
				{
					onSuccess: () => {
						// On 201 success: Start timer and polling
						setInitiationStatus("success");
						setTimerSeconds(kycPollingTime ?? 120); // feature flag or default to 2 minutes
						setIsTimerActive(true);
						setShouldPoll(true); // Enable polling
					},
					onError: (error) => {
						console.error("❌ KYC check initiation failed:", error);
						setInitiationStatus("error");
						toast.error(t`Something has gone wrong`);
						trackError(analytics, error, {
							component: "KycVerificationPage",
							action: "initiateKycCheck",
						});
					},
				},
			);
		},
		[initiateKycCheckMutation, kycPollingTime, analytics],
	);

	// Handle timer completion - primary handler for timeout
	const handleTimerComplete = useCallback(() => {
		// Update timer state
		setTimerSeconds(null); // Hide timer
		setIsTimerActive(false);
		// Handle timeout if still polling and not yet allowed to proceed
		if (shouldPoll && !checkResult?.isAllowedToProceed) {
			setShouldPoll(false); // Stop polling
			rampsActions.setKycCheckStatus("timeout");
		}
	}, [shouldPoll, checkResult]);

	// Handle check results (side effects in useEffect, not queryFn)
	useEffect(() => {
		if (!checkResult) return;

		// Map boolean response to status:
		// - isAllowedToProceed: true → ALLOW (complete)
		// - isStepUpRequired: true && !isAllowedToProceed → PENDING (still verifying)
		// - !isStepUpRequired && !isAllowedToProceed → DECLINE
		if (checkResult.isAllowedToProceed) {
			// ALLOW - User can proceed
			rampsActions.setKycCheckStatus("complete");
			rampsActions.setIsAllowedToProceed(true);
			rampsActions.setStepUpRequired(false);
			setShouldPoll(false); // Stop polling
			setIsTimerActive(false);
			setTimerSeconds(null); // Hide timer
		} else if (checkResult.isReviewPending) {
			// PENDING - Still needs verification
			if (kycCheckStatus !== "pending") {
				rampsActions.setKycCheckStatus("pending");
			}
			rampsActions.setStepUpRequired(true);
			rampsActions.setIsAllowedToProceed(false);
			// Polling continues automatically
		} else {
			// DECLINE - Not allowed to proceed
			rampsActions.setKycCheckStatus("declined");
			rampsActions.setStepUpRequired(false);
			rampsActions.setIsAllowedToProceed(false);
			setShouldPoll(false); // Stop polling
			setIsTimerActive(false);
			setTimerSeconds(null); // Hide timer
		}
	}, [checkResult, kycCheckStatus]);

	// Handle 2-minute timeout - fallback effect (primary handler is handleTimerComplete)
	useEffect(() => {
		if (timerSeconds === 0 && shouldPoll) {
			// Timeout reached while still polling - check if still pending (step-up required but not allowed)
			if (!checkResult?.isAllowedToProceed) {
				setShouldPoll(false); // Stop polling
				setIsTimerActive(false);
				setTimerSeconds(null); // Hide timer
				rampsActions.setKycCheckStatus("timeout");
				rampsActions.setStepUpRequired(false);
				rampsActions.setIsAllowedToProceed(false);
			}
		}
	}, [timerSeconds, shouldPoll, checkResult]);

	// Handle query errors
	useEffect(() => {
		if (isError && error) {
			console.error("❌ KYC check error:", error);
			toast.error(t`Error checking verification status. Please try again.`);
			trackError(analytics, error, {
				component: "KycVerificationPage",
				action: "kycCheckPolling",
			});
		}
	}, [isError, error, analytics]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			setShouldPoll(false); // TanStack Query will handle cleanup
		};
	}, []);

	// Auto-launch SDK when API data is available
	// Use ref to prevent multiple launch attempts
	useEffect(() => {
		if (hasApiData && !showSdk && !hasLaunchedSdkRef.current) {
			console.log("🚀 Auto-launching KYC SDK with API-provided data");
			setShowSdk(true);
			hasLaunchedSdkRef.current = true;
		}
	}, [hasApiData, showSdk]);

	// Handle SDK error
	const handleError = (error: Error) => {
		console.error("KYC verification error:", error);
		setSdkCompletionStatus("error");
		setShowSdk(false);
		hasLaunchedSdkRef.current = false; // Reset so user can retry
		toast.error("Something has gone wrong");
		navigate(
			navigateWithRampsState(search, {
				to: "/ramps/calculator",
				search: { step: "calculator" },
			}),
		);
	};

	// Handle user exit
	const handleUserExit = () => {
		setSdkCompletionStatus("exited");
		setShowSdk(false);
		hasLaunchedSdkRef.current = false; // Reset so user can retry
		toast.error("Something has gone wrong");
		navigate(
			navigateWithRampsState(search, {
				to: "/ramps/calculator",
				search: { step: "calculator" },
			}),
		);
	};

	// Determine if we have valid token and applicantId for SDK
	const hasValidSdkCredentials = !!(tokenForSdk && applicantIdForSdk);

	return (
		<div className="flex min-h-dvh flex-col bg-ramps-background-base">
			{/* Content */}
			<div className="flex flex-1 flex-col px-4">
				{!showSdk ? (
					<>
						<div className="flex flex-1 flex-col items-center justify-center text-center">
							<RampsText
								variant="body-default"
								color="primary"
								className="text-center"
							>
								{getKycMessage(kycCheckStatus, initiationStatus)}
							</RampsText>
							{/* Only show timer if initiation was successful and timer is active */}
							{initiationStatus === "success" &&
								isTimerActive &&
								kycCheckStatus !== "complete" &&
								kycCheckStatus !== "declined" &&
								kycCheckStatus !== "timeout" && (
									<KycTimer
										timerSeconds={timerSeconds}
										isTimerActive={isTimerActive}
										onTimerComplete={handleTimerComplete}
									/>
								)}
						</div>
						{isAllowedToProceed && (
							<div className="pt-8 pb-6">
								<RampsButton
									onClick={() => handleContinue(search)}
									className="h-12 w-full rounded-lg bg-black font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
								>
									{t`Continue`}
								</RampsButton>
							</div>
						)}
					</>
				) : (
					<>
						{/* SDK Container - Full Screen */}
						{/* Only render if we have valid token and applicantId to prevent mounting with empty values */}
						{hasValidSdkCredentials && (
							<div className="fixed inset-0 z-50">
								<KycVerification
									token={tokenForSdk}
									applicantId={applicantIdForSdk}
									onComplete={handleComplete}
									onError={handleError}
									onUserExit={handleUserExit}
								/>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}

export const Route = createFileRoute("/ramps/kyc-verification")({
	validateSearch: (search) =>
		search as {
			step?: string;
			transaction_id?: string;
			token?: string;
			accesstoken?: string;
		},
	component: KycVerificationPage,
});
